import React, {useCallback} from "react";
import {useIntl} from "react-intl";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "headlessui-v2";
import {twJoin} from "tailwind-merge";
import {XMarkIcon} from "@heroicons/react/20/solid";
import useProductInfo from "@widgets/pos/order/menu/product/context/ProductInfoProvider";
import ProductHeader from "@widgets/pos/order/menu/product/header/ProductHeader";
import ProductFlags from "@widgets/pos/order/menu/product/flags/ProductFlags";
import ProductOptionGroups from "@widgets/pos/order/menu/product/options/groups/ProductOptionGroups";
import ProductDepositIndication from "@widgets/pos/order/menu/product/deposit/ProductDepositIndication";
import ProductUnavailableIndication from "@widgets/pos/order/menu/product/availability/ProductUnavailableIndication";
import ProductAlcoholWarning from "@widgets/pos/order/menu/product/alcoholic/ProductAlcoholWarning";
import ProductAddToCart from "@widgets/pos/order/menu/product/cart/ProductAddToCart";

/**
 * Uses a Headless UI v2 dialog (instead of Peggirkit's v1 modal), so the
 * floating v2 listboxes inside don't count as outside clicks.
 */
const ProductInfoModal = () => {
    const intl = useIntl();
    const {product, show, setShow, loading} = useProductInfo();
    const close = useCallback(() => {
        if (!loading)
            setShow(false);
    }, [loading, setShow]);

    return (
        <Dialog
            open={show}
            onClose={close}
            className={"relative z-50"}
        >
            <DialogBackdrop
                transition
                className={"fixed inset-0 bg-gray-500/75 dark:bg-slate-600/75 transition-opacity duration-300 ease-out data-[closed]:opacity-0 data-[leave]:duration-200 data-[leave]:ease-in"}
            />

            <div className={"fixed inset-0 z-10 w-screen overflow-y-auto"}>
                <div className={"flex min-h-full items-center justify-center p-4 text-center"}>
                    <DialogPanel
                        transition
                        className={twJoin(
                            "relative w-full max-w-xl p-6 rounded-lg bg-white dark:bg-slate-800 text-left shadow-xl",
                            "transition-all duration-300 ease-out data-[closed]:opacity-0 data-[closed]:scale-95 data-[leave]:duration-200 data-[leave]:ease-in",
                        )}
                    >
                        {/* Close button */}
                        <div className={"absolute right-0 top-0 pr-4 pt-4"}>
                            <button
                                type={"button"}
                                onClick={close}
                                disabled={loading}
                                title={intl.formatMessage({id: "cancel"})}
                                className={twJoin(
                                    "text-gray-400 dark:text-slate-400 outline-0",
                                    loading
                                        ? "opacity-20 cursor-not-allowed"
                                        : "hover:text-gray-500 dark:hover:text-slate-300",
                                )}
                            >
                                <XMarkIcon className={"h-6 w-6"} aria-hidden={"true"}/>
                            </button>
                        </div>

                        <DialogTitle
                            as={"h3"}
                            className={"pr-8 text-base font-semibold leading-6 text-gray-900 dark:text-slate-100"}
                        >
                            {product.name}
                        </DialogTitle>

                        <div className={"mt-2 text-sm text-gray-500 dark:text-slate-400"}>
                            <article className={"text-left w-full flex flex-col gap-4 text-black dark:text-white"}>
                                <ProductHeader/>
                                <ProductFlags/>
                                <ProductOptionGroups/>
                                <ProductDepositIndication/>
                                <ProductUnavailableIndication/>
                                <ProductAlcoholWarning/>
                                <ProductAddToCart/>
                            </article>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};

export default ProductInfoModal;